import React from 'react'
import {
  GetEstateVisitsQuery,
  useDeleteVisitMutation,
  useUpdateVisitIsVisitDoneMutation,
  useUpdateVisitTimeMutation,
} from '@gql'
import { EstateVisitDoneButton } from './EstateVisitDoneButton'
import { EstateVisitDeleteButton } from './EstateVisitDeleteButton'
import { EstateVisitUpdateButton } from './EstateVisitUpdateButton'
import { toast } from '@/components/ui/use-toast'
import { useTranslation } from 'react-i18next'
import useCurrentSeller from '@/seller/hooks/useCurrentSeller'

type EstateVisitRowProps = {
  visit: GetEstateVisitsQuery['visit'][number]
  visitSlotDuration: number
}
export const EstateVisitRow = ({
  visit,
  visitSlotDuration,
}: EstateVisitRowProps) => {
  const { t } = useTranslation()

  // Sellers can't update visits, only admins can
  const seller = useCurrentSeller()
  const readOnly = seller != null

  const [deleteVisit, { loading: deleteVisitLoading }] =
    useDeleteVisitMutation()

  const [updateVisitIsVisitDone, { loading: updateVisitIsVisitDoneLoading }] =
    useUpdateVisitIsVisitDoneMutation()

  const [updateVisitTime, { loading: updateVisitTimeLoading }] =
    useUpdateVisitTimeMutation()

  const handleDelete = async (visitId: string) => {
    await deleteVisit({
      variables: {
        id: visitId,
      },
      refetchQueries: ['getEstateVisits'],
      onCompleted: () => {
        toast({
          description: t('EstateVisitDeleteButton.toast.success'),
          variant: 'success',
        })
      },
      onError(error) {
        console.error(error)
        toast({
          description: t('EstateVisitDeleteButton.toast.error'),
          variant: 'destructive',
        })
      },
    })
  }

  const handleMarkAsDone = async (visitId: string, isVisitDone: boolean) => {
    await updateVisitIsVisitDone({
      variables: {
        id: visitId,
        isVisitDone,
      },
      refetchQueries: ['getEstateVisits'],
      onCompleted: () => {
        toast({
          description: t('EstateVisitDoneButton.toast.success'),
          variant: 'success',
        })
      },
      onError(error) {
        console.error(error)
        toast({
          description: t('EstateVisitDoneButton.toast.error'),
          variant: 'destructive',
        })
      },
    })
  }

  const handleUpdateVisitTime = async (
    visitId: string,
    startTime: Date,
    endTime: Date
  ) => {
    await updateVisitTime({
      variables: {
        id: visitId,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
      },
      refetchQueries: ['getEstateVisits'],
      onCompleted: () => {
        toast({
          description: t('EstateVisitUpdateButton.toast.success'),
          variant: 'success',
        })
      },
      onError(error) {
        console.error(error)
        toast({
          description: t('EstateVisitUpdateButton.toast.error'),
          variant: 'destructive',
        })
      },
    })
  }

  /*   const handleCreateVisit = (createData: CreateVisitMutationVariables) => {
    // Simply create the visit via GraphQL + refetchQueries: ['getEstateVisits'],
  }

  const handleUpdateVisitDate = (visitId: string, updateData: Update) => {
    // Simply update the visit via GraphQL + refetchQueries: ['getEstateVisits'],
  } 
    */

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <div key={visit.id} className="tw-flex tw-gap-2">
        <div className="tw-text-zinc-900 tw-font-medium tw-flex tw-content-center tw-items-center">
          <p className="tw-text-zinc-900 tw-font-medium">
            {visit.buyer_member.user?.displayName}
          </p>
        </div>
        <EstateVisitDoneButton
          enabled={!readOnly && !updateVisitIsVisitDoneLoading}
          isVisitDone={visit.isVisitDone}
          onConfirm={(isVisitDone: boolean) =>
            handleMarkAsDone(visit.id, isVisitDone)
          }
        />
        {!visit.isVisitDone && !readOnly && (
          <>
            <EstateVisitUpdateButton
              enabled={!updateVisitTimeLoading}
              currentStartTime={visit.startTime}
              visitSlotDuration={visitSlotDuration}
              onSubmit={(startTime: Date, endTime: Date) =>
                handleUpdateVisitTime(visit.id, startTime, endTime)
              }
            />
            <EstateVisitDeleteButton
              onConfirm={() => handleDelete(visit.id)}
              enabled={!deleteVisitLoading}
            />
          </>
        )}
      </div>
    </div>
  )
}
